
import { useState, useCallback, useRef, useEffect } from 'react';
import { useSwipeable, SwipeableHandlers } from 'react-swipeable';

// Interface definition for swipe configuration:
export interface SwipeConfig {
  horizontal: {
    onLeft: () => void;
    onRight: () => void;
    enabled: boolean;
  };
  vertical: {
    onUp: () => void;
    onDown: () => void;
    enabled: boolean;
  };
  sensitivity: {
    delta: number;
    velocity: number;
  };
  preventConflicts: boolean;
}

// Swipe state tracking (internal only)
export interface SwipeState {
  isActive: boolean;
  direction: 'horizontal' | 'vertical' | null;
  currentOffset: { x: number; y: number };
  startPosition: { x: number; y: number };
  debugInfo?: string;
}

// Main Hook:
export const useAdvancedSwipeSystem = (config: SwipeConfig) => {
  console.log('🎯 useAdvancedSwipeSystem initialized with config:', config);

  const [swipeState, setSwipeState] = useState<SwipeState>({
    isActive: false,
    direction: null,
    currentOffset: { x: 0, y: 0 },
    startPosition: { x: 0, y: 0 },
    debugInfo: 'Initialized',
  });

  const startPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const panelElementRef = useRef<HTMLElement | null>(null);
  const isScrollingRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  const resetSwipeState = useCallback(() => {
    console.log('🔄 Resetting swipe state');
    setSwipeState({
      isActive: false,
      direction: null,
      currentOffset: { x: 0, y: 0 },
      startPosition: { x: 0, y: 0 },
      debugInfo: 'Reset',
    });
    isScrollingRef.current = false;
  }, []);

  // Check if the target element or its parents are scrollable
  const isElementScrollable = useCallback((element: Element): boolean => {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    const isScrollable = style.overflowY === 'scroll' || style.overflowY === 'auto';
    const hasScrollableContent = element.scrollHeight > element.clientHeight;
    
    const result = isScrollable && hasScrollableContent;
    console.log('📊 Checking scrollability:', {
      element: element.tagName,
      className: element.className,
      isScrollable,
      hasScrollableContent,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
      result
    });
    
    return result;
  }, []);

  // Check if user is interacting with scrollable content or floating action bar
  const isInteractingWithScrollableContent = useCallback((target: Element): boolean => {
    console.log('🎯 Checking if interacting with scrollable content, starting from:', target.tagName, target.className);
    
    // Check if target is within floating action bar area (exclude from swipe handling)
    if (isWithinFloatingActionBar(target)) {
      console.log('🚫 Target is within floating action bar, excluding from swipe');
      return false;
    }
    
    let current = target;
    let depth = 0;
    
    while (current && current !== document.body && depth < 10) {
      console.log(`  Level ${depth}: ${current.tagName}${current.className ? '.' + current.className : ''}`);
      
      if (isElementScrollable(current)) {
        console.log('✅ Found scrollable parent at level', depth, ':', current.tagName, current.className);
        return true;
      }
      current = current.parentElement!;
      depth++;
    }
    
    console.log('❌ No scrollable parent found');
    return false;
  }, [isElementScrollable]);

  // Check if target is within floating action bar area or interactive button
  const isWithinFloatingActionBar = useCallback((target: Element): boolean => {
    let current = target;
    let depth = 0;
    
    // First check if it's a button element or has button role
    if (current.tagName === 'BUTTON' || current.getAttribute('role') === 'button') {
      console.log('🚫 Found button element:', current.tagName, current.className);
      return true;
    }
    
    while (current && current !== document.body && depth < 5) {
      // Check for button elements
      if (current.tagName === 'BUTTON' || current.getAttribute('role') === 'button') {
        console.log('🚫 Found button element in hierarchy:', current.tagName, current.className);
        return true;
      }
      
      // Check for floating action bar container classes
      const className = current.className || '';
      if (className.includes('fixed') && className.includes('right-4') && className.includes('flex-col-reverse')) {
        console.log('🎯 Found floating action bar container:', current.tagName, className);
        return true;
      }
      
      // Check for floating action button classes
      if (className.includes('rounded-full') && className.includes('w-12') && className.includes('h-12')) {
        console.log('🎯 Found floating action button:', current.tagName, className);
        return true;
      }
      
      current = current.parentElement!;
      depth++;
    }
    
    return false;
  }, []);

  // Find the scrollable parent element
  const findScrollableParent = useCallback((element: Element): Element | null => {
    console.log('🔍 Finding scrollable parent for:', element.tagName, element.className);
    
    let current = element;
    let depth = 0;
    
    while (current && current !== document.body && depth < 10) {
      if (isElementScrollable(current)) {
        console.log('✅ Found scrollable parent:', current.tagName, current.className);
        return current;
      }
      current = current.parentElement!;
      depth++;
    }
    
    console.log('❌ No scrollable parent found');
    return null;
  }, [isElementScrollable]);

  // Check if scrollable element is at scroll boundary
  const isAtScrollBoundary = useCallback((element: Element, direction: 'up' | 'down'): boolean => {
    if (!isElementScrollable(element)) {
      console.log('🚫 Element not scrollable, not at boundary');
      return false;
    }
    
    const { scrollTop, scrollHeight, clientHeight } = element;
    const threshold = 10; // Increased threshold for better detection
    
    let atBoundary = false;
    
    if (direction === 'up') {
      // At top if scrollTop is 0 or very close to 0
      atBoundary = scrollTop <= threshold;
    } else {
      // At bottom if scrolled to the end
      atBoundary = scrollTop >= scrollHeight - clientHeight - threshold;
    }
    
    console.log('🎯 Boundary check:', {
      direction,
      scrollTop,
      scrollHeight,
      clientHeight,
      threshold,
      atBoundary,
      calculation: direction === 'up' ? `${scrollTop} <= ${threshold}` : `${scrollTop} >= ${scrollHeight - clientHeight - threshold}`
    });
    
    return atBoundary;
  }, [isElementScrollable]);

  // Handle scroll within scrollable content
  const handleScrollableSwipe = useCallback((element: Element, deltaY: number) => {
    const scrollAmount = Math.abs(deltaY) * 0.5; // Reduced scroll sensitivity for smoother experience
    const scrollDirection = deltaY > 0 ? scrollAmount : -scrollAmount;
    
    console.log('📜 Handling scrollable swipe:', {
      deltaY,
      scrollAmount,
      scrollDirection,
      currentScrollTop: element.scrollTop
    });
    
    // Store current scroll position to detect boundaries
    lastScrollTopRef.current = element.scrollTop;
    
    // Apply scroll (negative because swipe down should scroll down)
    element.scrollTop -= scrollDirection;
    
    // Mark as actively scrolling
    isScrollingRef.current = true;
    
    console.log('📜 After scroll:', {
      newScrollTop: element.scrollTop,
      wasScrolling: isScrollingRef.current
    });
  }, []);

  const swipeHandlers: SwipeableHandlers = useSwipeable({
    onSwipeStart: (eventData) => {
      const { initial, event } = eventData;
      const target = event.target as Element;
      
      console.log('🚀 Swipe started:', {
        initial,
        target: target?.tagName,
        targetClass: target?.className
      });
      
      // Prevent swipe if interacting with buttons or floating action bar
      if (isWithinFloatingActionBar(target)) {
        console.log('🚫 Preventing swipe - target is button or floating action element');
        event.stopPropagation();
        return;
      }
      
      startPositionRef.current = { x: initial[0], y: initial[1] };
      isScrollingRef.current = false;
      
      setSwipeState(prev => ({
        ...prev,
        isActive: true,
        startPosition: { x: initial[0], y: initial[1] },
        debugInfo: 'Swipe started',
      }));
    },

    onSwiping: (eventData) => {
      const { deltaX, deltaY, event } = eventData;
      const target = event.target as Element;

      // Prevent swiping if interacting with buttons or floating action bar
      if (isWithinFloatingActionBar(target)) {
        console.log('🚫 Preventing swipe motion - target is button or floating action element');
        event.stopPropagation();
        return;
      }

      const direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      
      console.log('👆 Swiping:', {
        deltaX,
        deltaY,
        direction,
        target: target.tagName,
        targetClass: target.className
      });

      // If this is a vertical swipe and user is on scrollable content
      if (direction === 'vertical' && config.vertical.enabled && isInteractingWithScrollableContent(target)) {
        const scrollableParent = findScrollableParent(target);
        if (scrollableParent) {
          // Check if we're at boundaries before scrolling
          const swipeDirection = deltaY > 0 ? 'down' : 'up';
          const atBoundary = isAtScrollBoundary(scrollableParent, swipeDirection);
          
          console.log('🎯 Vertical swipe on scrollable content:', {
            swipeDirection,
            atBoundary,
            deltaY
          });
          
          if (!atBoundary) {
            // Not at boundary, handle as scroll
            console.log('📜 Scrolling content (not at boundary)');
            handleScrollableSwipe(scrollableParent, deltaY);
          } else {
            console.log('🎯 At boundary, will trigger gesture on swipe end');
          }
          // If at boundary, let it fall through to gesture handling
        }
      } else if (direction === 'horizontal') {
        console.log('↔️ Horizontal swipe detected');
      }

      setSwipeState(prev => ({
        ...prev,
        direction,
        currentOffset: { x: deltaX, y: deltaY },
        debugInfo: `Swiping ${direction}, delta: ${deltaX}, ${deltaY}`,
      }));
    },

    onSwiped: (eventData) => {
      const { deltaX, deltaY, velocity, event } = eventData;
      const target = event.target as Element;
      const direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';

      console.log('✋ Swipe ended:', {
        deltaX,
        deltaY,
        velocity,
        direction,
        target: target.tagName,
        targetClass: target.className,
        config: {
          deltaThreshold: config.sensitivity.delta,
          velocityThreshold: config.sensitivity.velocity / 1000
        }
      });

      // Prevent swipe completion if interacting with buttons or floating action bar
      if (isWithinFloatingActionBar(target)) {
        console.log('🚫 Preventing swipe completion - target is button or floating action element');
        event.stopPropagation();
        resetSwipeState();
        return;
      }

      // Handle horizontal gestures (navigation)
      if (direction === 'horizontal' && config.horizontal.enabled) {
        const meetsThreshold = Math.abs(deltaX) > config.sensitivity.delta && Math.abs(velocity) > config.sensitivity.velocity / 1000;
        
        console.log('↔️ Horizontal gesture check:', {
          deltaX,
          velocity,
          meetsThreshold,
          deltaThreshold: config.sensitivity.delta,
          velocityThreshold: config.sensitivity.velocity / 1000
        });
        
        if (meetsThreshold) {
          if (deltaX < 0) {
            console.log('⬅️ Triggering left swipe action');
            config.horizontal.onLeft();
          } else {
            console.log('➡️ Triggering right swipe action');
            config.horizontal.onRight();
          }
        } else {
          console.log('↔️ Horizontal gesture did not meet threshold');
        }
      } 
      // Handle vertical gestures
      else if (direction === 'vertical' && config.vertical.enabled) {
        const meetsThreshold = Math.abs(deltaY) > config.sensitivity.delta && Math.abs(velocity) > config.sensitivity.velocity / 1000;
        
        console.log('↕️ Vertical gesture check:', {
          deltaY,
          velocity,
          meetsThreshold,
          deltaThreshold: config.sensitivity.delta,
          velocityThreshold: config.sensitivity.velocity / 1000
        });
        
        if (!meetsThreshold) {
          console.log('↕️ Vertical gesture did not meet threshold');
          resetSwipeState();
          return;
        }

        // Check if we're interacting with scrollable content
        if (isInteractingWithScrollableContent(target)) {
          const scrollableParent = findScrollableParent(target);
          if (scrollableParent) {
            const swipeDirection = deltaY > 0 ? 'down' : 'up';
            
            // Only execute gesture actions if we're at a scroll boundary
            if (isAtScrollBoundary(scrollableParent, swipeDirection)) {
              console.log('🎯 At scroll boundary, executing gesture action');
              if (deltaY < 0) {
                // Swipe up at top boundary
                console.log('⬆️ Triggering up swipe action');
                config.vertical.onUp();
              } else {
                // Swipe down at bottom boundary  
                console.log('⬇️ Triggering down swipe action');
                config.vertical.onDown();
              }
            } else {
              console.log('📜 Not at scroll boundary, scrolling was already handled');
            }
            // If not at boundary, scrolling was already handled in onSwiping
          }
        } else {
          // Not interacting with scrollable content, execute normal gesture
          console.log('🎯 No scrollable content, executing gesture action');
          if (deltaY < 0) {
            console.log('⬆️ Triggering up swipe action');
            config.vertical.onUp();
          } else {
            console.log('⬇️ Triggering down swipe action');
            config.vertical.onDown();
          }
        }
      }

      resetSwipeState();
    },

    trackMouse: true,
    trackTouch: true,
    preventScrollOnSwipe: false, // Allow native scrolling
    delta: config.sensitivity.delta,
  });

  // Debug effect to log when config changes
  useEffect(() => {
    console.log('🔄 SwipeConfig updated:', config);
  }, [config]);

  // Expose enhanced swipe handlers with ref assignment
  const enhancedSwipeHandlers = {
    ...swipeHandlers,
    ref: (element: HTMLElement | null) => {
      console.log('🔗 Swipe handlers attached to element:', element?.tagName, element?.className);
      panelElementRef.current = element;
      if (swipeHandlers.ref) {
        swipeHandlers.ref(element);
      }
    },
  };

  console.log('🎯 useAdvancedSwipeSystem returning handlers and state:', {
    hasHandlers: !!enhancedSwipeHandlers,
    swipeState
  });

  return {
    swipeHandlers: enhancedSwipeHandlers,
    swipeState,
    resetSwipeState,
  };
};
